import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, DirectionsRenderer } from '@react-google-maps/api';
import axios from 'axios';
import { useSocketTracking, useDriverLocation } from '../hooks/useSocketTracking';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Phone, 
  ArrowLeft, 
  MapPin, 
  Clock, 
  Navigation,
  CheckCircle,
  Loader2,
  Play,
  Flag,
  UserCheck,
  Trophy
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 20.5937,
  lng: 78.9629
};

// Status configuration
const statusConfig = {
  accepted: {
    color: 'bg-yellow-500',
    label: 'Ready to Start',
    nextAction: 'Start Ride',
    nextIcon: Play,
    nextEndpoint: 'start'
  },
  started: {
    color: 'bg-blue-500',
    label: 'On the way',
    nextAction: "I've Arrived",
    nextIcon: Flag,
    nextEndpoint: 'arrive'
  },
  arrived: {
    color: 'bg-green-500',
    label: 'At Pickup',
    nextAction: 'Pick Up Rider',
    nextIcon: UserCheck,
    nextEndpoint: 'pickup'
  },
  picked_up: {
    color: 'bg-purple-500',
    label: 'In Transit',
    nextAction: 'Complete Ride',
    nextIcon: Trophy,
    nextEndpoint: 'complete'
  },
  completed: {
    color: 'bg-gray-500',
    label: 'Completed',
    nextAction: null,
    nextIcon: null,
    nextEndpoint: null
  }
};

export default function DriverTrackingPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [user, setUser] = useState(null);
  const [rideInfo, setRideInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('accepted');
  const [directions, setDirections] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const lastLocationRef = useRef(null);
  
  // Get user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);
  
  // Socket.IO connection
  const {
    isConnected,
    sendLocationUpdate,
    sendEtaUpdate
  } = useSocketTracking(requestId, 'driver', user?.id);
  
  // Location callback for sending updates
  const handleLocationUpdate = useCallback((location) => {
    // Only send if location changed significantly (>10 meters)
    if (lastLocationRef.current) {
      const distance = calculateDistance(
        lastLocationRef.current.latitude,
        lastLocationRef.current.longitude,
        location.latitude,
        location.longitude
      );
      if (distance < 10) return; // Skip if moved less than 10m
    }
    
    lastLocationRef.current = location;
    
    // Send via Socket.IO
    sendLocationUpdate(
      location.latitude,
      location.longitude,
      location.heading,
      location.speed
    );
    
    // Also send to REST API for persistence
    const token = localStorage.getItem('token');
    axios.post(
      `${API_URL}/api/carpool/requests/${requestId}/location`,
      null,
      {
        params: {
          latitude: location.latitude,
          longitude: location.longitude,
          heading: location.heading || 0,
          speed: location.speed || 0
        },
        headers: { Authorization: `Bearer ${token}` }
      }
    ).catch(err => console.error('Failed to persist location:', err));
  }, [requestId, sendLocationUpdate]);
  
  // Driver location tracking
  const {
    currentLocation,
    locationError,
    isWatching
  } = useDriverLocation(requestId, isTracking, handleLocationUpdate);
  
  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };
  
  // Fetch ride info
  const fetchRideInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/carpool/requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Find this specific request in incoming requests (driver is recipient)
      const request = response.data.incoming.find(r => r._id === requestId);
      
      if (request) {
        setRideInfo(request);
        setCurrentStatus(request.status);
        
        // Start tracking if ride is in progress
        if (['started', 'arrived', 'picked_up'].includes(request.status)) {
          setIsTracking(true);
        }
      } else {
        throw new Error('Ride not found');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching ride info:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to load ride info',
        variant: 'destructive'
      });
      setLoading(false);
    }
  }, [requestId, toast]);
  
  useEffect(() => {
    if (requestId) {
      fetchRideInfo();
    }
  }, [requestId, fetchRideInfo]);
  
  // Get pickup location (rider's location)
  const getPickupLocation = () => {
    if (!rideInfo?.requester) return defaultCenter;
    const loc = rideInfo.requester;
    if (loc.latitude && loc.longitude) {
      return { lat: loc.latitude, lng: loc.longitude };
    }
    return defaultCenter;
  };
  
  // Get current driver position
  const getDriverPosition = () => {
    if (currentLocation) {
      return { lat: currentLocation.latitude, lng: currentLocation.longitude };
    }
    if (user?.location) {
      if (user.location.coordinates) {
        return { lat: user.location.coordinates[1], lng: user.location.coordinates[0] };
      }
      return { lat: user.location.latitude, lng: user.location.longitude };
    }
    return null;
  };
  
  // Calculate directions
  useEffect(() => {
    const driverPos = getDriverPosition();
    const pickupLoc = getPickupLocation();
    
    if (!driverPos || !window.google || currentStatus === 'completed') return;
    
    const directionsService = new window.google.maps.DirectionsService();
    
    directionsService.route(
      {
        origin: driverPos,
        destination: pickupLoc,
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === 'OK') {
          setDirections(result);
          
          // Update ETA
          const duration = result.routes[0]?.legs[0]?.duration?.value; // in seconds
          const distance = result.routes[0]?.legs[0]?.distance?.value; // in meters
          if (duration && distance) {
            sendEtaUpdate(Math.ceil(duration / 60), distance);
          }
        }
      }
    );
  }, [currentLocation, rideInfo, currentStatus]);
  
  // Handle action button click (Start, Arrive, Pickup, Complete)
  const handleAction = async () => {
    const config = statusConfig[currentStatus];
    if (!config.nextEndpoint) return;
    
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/carpool/requests/${requestId}/${config.nextEndpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update status locally
      const statusOrder = ['accepted', 'started', 'arrived', 'picked_up', 'completed'];
      const nextIndex = statusOrder.indexOf(currentStatus) + 1;
      const newStatus = statusOrder[nextIndex];
      setCurrentStatus(newStatus);
      
      // Start/stop tracking based on status
      if (newStatus === 'started') {
        setIsTracking(true);
      } else if (newStatus === 'completed') {
        setIsTracking(false);
        toast({
          title: 'Success!',
          description: 'Ride completed successfully. Thanks for sharing the ride!',
        });
        // Navigate back after a short delay
        setTimeout(() => navigate('/carpool'), 2000);
      }
      
      toast({
        title: 'Success',
        description: `Status updated to: ${statusConfig[newStatus]?.label || newStatus}`,
      });
    } catch (err) {
      console.error('Action failed:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to update status',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle call rider
  const handleCallRider = () => {
    if (rideInfo?.requester?.phone) {
      window.location.href = `tel:${rideInfo.requester.phone}`;
    }
  };
  
  // Navigate to pickup in Google Maps
  const handleNavigate = () => {
    const pickup = getPickupLocation();
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${pickup.lat},${pickup.lng}&travelmode=driving`,
      '_blank'
    );
  };
  
  // Map load callback
  const onMapLoad = useCallback((map) => {
    setMapRef(map);
    setMapsLoaded(true);
  }, []);
  
  // Fit bounds when we have both markers
  useEffect(() => {
    if (mapRef && currentLocation && rideInfo?.requester) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(getDriverPosition());
      bounds.extend(getPickupLocation());
      mapRef.fitBounds(bounds, { padding: 50 });
    }
  }, [mapRef, currentLocation, rideInfo]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading ride information...</p>
        </div>
      </div>
    );
  }

  const config = statusConfig[currentStatus] || statusConfig.accepted;
  const ActionIcon = config.nextIcon;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/carpool')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold">Driver Mode</h1>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
                <span className="text-sm text-gray-500">
                  {isWatching ? 'Tracking active' : isConnected ? 'Connected' : 'Connecting...'}
                </span>
              </div>
            </div>
          </div>
          <Badge className={config.color}>
            {config.label}
          </Badge>
        </div>
      </div>
      
      {/* Map Container */}
      <div className="flex-1 relative">
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={getDriverPosition() || getPickupLocation()}
            zoom={15}
            onLoad={onMapLoad}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false
            }}
          >
            {/* Driver (You) Marker */}
            {mapsLoaded && getDriverPosition() && (
              <Marker
                position={getDriverPosition()}
                icon={{
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="18" fill="#3B82F6" stroke="white" stroke-width="3"/>
                      <path d="M20 12 L26 24 L20 22 L14 24 Z" fill="white"/>
                    </svg>
                  `),
                  anchor: new window.google.maps.Point(20, 20),
                  scaledSize: new window.google.maps.Size(40, 40),
                  rotation: currentLocation?.heading || 0
                }}
                title="You"
              />
            )}
            
            {/* Pickup Location Marker */}
            {mapsLoaded && (
              <Marker
                position={getPickupLocation()}
                icon={{
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 0 C31 0 40 9 40 20 C40 35 20 50 20 50 C20 50 0 35 0 20 C0 9 9 0 20 0" fill="#22C55E"/>
                      <circle cx="20" cy="18" r="8" fill="white"/>
                    </svg>
                  `),
                  anchor: new window.google.maps.Point(20, 50),
                  scaledSize: new window.google.maps.Size(40, 50)
                }}
                title="Pickup Location"
              />
            )}
            
            {/* Route */}
            {directions && currentStatus !== 'completed' && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#3B82F6',
                    strokeWeight: 5,
                    strokeOpacity: 0.8
                  }
                }}
              />
            )}
          </GoogleMap>
        </LoadScript>
        
        {/* Location Error */}
        {locationError && (
          <div className="absolute top-4 left-4 right-4">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-3">
                <p className="text-red-700 text-sm">{locationError}</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* ETA Card */}
        {directions && currentStatus === 'started' && (
          <div className="absolute top-4 left-4 right-4">
            <Card className="bg-white/95 backdrop-blur">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-semibold">
                        {directions.routes[0]?.legs[0]?.duration?.text || '...'} to rider
                      </p>
                      <p className="text-sm text-gray-500">
                        {directions.routes[0]?.legs[0]?.distance?.text || 'Calculating...'}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={handleNavigate}>
                    <Navigation className="h-4 w-4 mr-1" />
                    Navigate
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Completed Overlay */}
        {currentStatus === 'completed' && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Card className="mx-4">
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">Ride Completed!</h2>
                <p className="text-gray-600 mb-4">Thanks for sharing the ride.</p>
                <Button onClick={() => navigate('/carpool')}>
                  Back to Carpool
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Bottom Card - Rider Info & Actions */}
      <div className="bg-white shadow-lg rounded-t-3xl">
        <div className="p-4 space-y-4">
          {/* Rider Info */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
              <MapPin className="h-7 w-7 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{rideInfo?.requester?.name || 'Rider'}</h3>
              <p className="text-gray-600 text-sm">Pickup location</p>
            </div>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleCallRider}
              disabled={!rideInfo?.requester?.phone}
            >
              <Phone className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Main Action Button */}
          {config.nextAction && currentStatus !== 'completed' && (
            <Button 
              className="w-full h-14 text-lg"
              onClick={handleAction}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : ActionIcon && (
                <ActionIcon className="h-5 w-5 mr-2" />
              )}
              {config.nextAction}
            </Button>
          )}
          
          {/* Quick Actions */}
          {currentStatus !== 'completed' && (
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleCallRider}
                disabled={!rideInfo?.requester?.phone}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Rider
              </Button>
              <Button 
                variant="outline"
                className="flex-1"
                onClick={handleNavigate}
              >
                <Navigation className="h-4 w-4 mr-2" />
                Navigate
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
